import { Component, Input, Output, EventEmitter, ViewChild } from "@angular/core";
import { IgxActionStripComponent } from "@infragistics/igniteui-angular";
import { Utilities } from "@tylertech/shared-module";
import { IGridViewMetadata, IMetadataAction } from "@tylertech/tyler-api-types";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import {
    GridActionService,
    MetadataViewService,
    MetadataTableService,
    MultipleEntityEditService,
    MetadataService,
    MetadataFormService,
    GridSelectionService,
    InfoGridLayoutService,
    ProviderStorageService,
    GridLoadService,
    MetadataPropertyService,
} from "../../../services";
import { RowCommandArgs, RepositoryFormData, RowCommandRow } from "../../../interfaces";
import { PopoverComponent } from "@tylertech/forge";
@Component({
    selector: "row-actions",
    templateUrl: "./rowActions.component.tmpl.html",
    standalone: false
})
export class RowActionsComponent {
    @Input()
    actionStrip: IgxActionStripComponent;

    @Input()
    providerId: string;

    @Input()
    cellEdit: boolean;

    @Input()
    useRepository: boolean;

    @Input()
    disableViewAction: boolean;

    @Input()
    datacontroller: RepositoryFormData;

    @Input()
    multipleEdit: boolean;

    @Input()
    viewMetadata: IGridViewMetadata;

    @Input()
    refreshAction: boolean;

    @Output()
    refreshActionChange = new EventEmitter<boolean>();

    @Output()
    repositoryFunction = new EventEmitter<RowCommandArgs>();

    @ViewChild("overFlowMenu") overFlowMenu: PopoverComponent;

    private contextMenuHandlers = new WeakMap<HTMLElement, EventListener>();

    primaryActions: IMetadataAction[];

    overflowActions: IMetadataAction[];

    model: any;

    canEdit: boolean = false;

    canDelete: boolean = false;

    // eslint-disable-next-line @typescript-eslint/method-signature-style
    readonly bindFetchRowModel: () => Promise<any>;
    constructor(
        private readonly $commonDialogsService: CommonDialogsService,
        private $metadataService: MetadataService,
        private $metadataFormService: MetadataFormService,
        private $metadataTableService: MetadataTableService,
        private $providerStorageService: ProviderStorageService,
        private readonly $metadataViewService: MetadataViewService,
        private $multipleEntityEditService: MultipleEntityEditService,
        private $gridSelectionService: GridSelectionService,
        private $gridLoadService: GridLoadService,
        private $gridActionService: GridActionService,
        private $gridLayoutService: InfoGridLayoutService,
        private readonly $metadataPropertyService: MetadataPropertyService,
    ) {
        this.bindFetchRowModel = this.fetchRowModel.bind(this);
    }

    private _row: any;
    get row(): any {
        const current = this.actionStrip?.context?.grid.data?.find(x => x.Id === this.actionStrip?.context?.data.Id);
        if (current && (this.refreshAction || current.Id !== this._row?.Id))
        {
            if (this.refreshAction)
            {
                this.refreshAction = false;
                requestAnimationFrame(() => this.refreshActionChange.emit(false));
            }

            this._row = current;
            this.initializeRowActions();
        }
        return current;
    }

    get isViewOnly(): boolean {
        if (!this.row) return false;
        return (
            this.viewMetadata.ViewOnly ||
            this.viewMetadata.EditForm?.ViewOnly ||
            this.viewMetadata.EditFormViewOnly
        );
    }

    get canView(): boolean {
        if (!this.row) return false;
        return this.isViewOnly && !this.disableViewAction;
    }

    get hasFeatureAction(): boolean {
        return !Utilities.IsNullOrUndefined(this.row?.FeatureAction) && !this.row.FeatureAction.Hidden;
    }

    get hasOverflowActions(): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(this.overflowActions);
    }

    initializeRowActions(): void {
        const hasCommands =
            !Utilities.IsNullOrUndefinedOrEmpty(this.row.Commands) &&
            this.row.Commands.filter(x => !x.Hidden).length > 0;
        if (!hasCommands) {
            this.primaryActions = null;
            this.overflowActions = null;
        }
        if (this.row)
        {
            this.canEdit = this.canDo("Edit");
            this.canDelete = this.canDo("Delete");
        }
        if (!hasCommands && !this.hasFeatureAction)
            return;
        this.primaryActions = this.row.Commands.filter(x => x.Primary && !x.Hidden);
        this.model = this.row["$$model"];
        this.overflowActions = <IMetadataAction[]>this.row.Commands.filter(x => !x.Primary && !x.Hidden);
    }

    performAction(action: IMetadataAction, $event?: Event): void
    {
        if (!Utilities.IsNullOrUndefined($event)) {
            $event.stopPropagation();
        }
        this.overFlowMenu.open = false;
        if (this.useRepository && (action.Label === "Copy" || action.Caption === "Copy")) {
            this.repositoryFunction.emit(<RowCommandArgs>{
                command: "Copy",
                row: <RowCommandRow>{
                    id: this.row.Id,
                    model: this.row["$$model"],
                },
            });
        } else {
            this.fetchRowModel().then(model => {
                this.row["$$model"] = model;
                this.$gridLoadService.fireModelChangedListeners(this.providerId);
                action.Disabled = true;
                this.$gridActionService
                    .PerformMetadataAction(
                        this.providerId,
                        this.row,
                        action,
                        this.datacontroller,
                        this.viewMetadata,
                        this.repositoryFunction
                    )
                    .finally(() => (action.Disabled = false));
            });
        }
    }

    fetchRowModel(): Promise<any>
    {
        return this.$metadataTableService.FetchRowModel(this.row, this.datacontroller, this.viewMetadata);
    }

    getDataController(action: IMetadataAction, type: string): RepositoryFormData {
        if (Utilities.IsNullOrUndefined(action)) new RepositoryFormData(null, null);
        if (!Utilities.IsNullOrUndefined(this.datacontroller)) return this.datacontroller;
        if (!Utilities.IsNullOrUndefined(action.ViewToShow))
            type = this.$metadataViewService.GetTypeName(action.ViewToShow);
        return new RepositoryFormData(type, null);
    }

    middleClick(event: MouseEvent, row: any, viewOnly: boolean): void {
        if (event.button === 1 && this.allowEditInNewTab()) {
            this.viewMetadata["$editInNewTab"] = true;
            this.edit(row, viewOnly);
        }
    }

    onContextMenu(event: MouseEvent, row: any, viewOnly: boolean): void
    {
        const self = this;
        event.preventDefault();
        if (!this.allowEditInNewTab()) return;
        const openPopovers = document.getElementsByTagName("forge-popover");
        for (let i = 0, l = openPopovers.length; i < l; i++)
        {
            if (openPopovers[i].open)
                document.body.removeChild(openPopovers[i]);
        }

        // Popover
        const popover = document.createElement("forge-popover");
        popover.anchorElement = (<HTMLElement>event.currentTarget).closest("igx-grid-row") as HTMLElement;
        popover.placement = "bottom-end";
        popover.offset = { mainAxis: -20 };
        popover.positionStrategy = "fixed";
        // List
        const optionList = document.createElement("forge-list");
        optionList.dense = true;
        popover.appendChild(optionList);

        const execute = (): void =>
        {
            self.viewMetadata["$editInNewTab"] = true;
            self.edit(row, viewOnly);
        };

        const button = document.createElement("button");
        button.type = "button";
        button.innerText = "Open in New Tab";
        button.addEventListener("click", execute);
        self.contextMenuHandlers.set(button, execute);
        const li = document.createElement("forge-list-item");
        li.appendChild(button);
        const listItem = optionList.appendChild(li);

        const listItemHandler = () => closePopover();
        listItem.addEventListener("click", listItemHandler);
        self.contextMenuHandlers.set(listItem, listItemHandler);

        const closePopover = (): void =>
        {
            const allButtons = popover.querySelectorAll("button");
            allButtons.forEach(btn =>
            {
                const handler = self.contextMenuHandlers.get(btn);
                if (handler)
                {
                    btn.removeEventListener("click", handler);
                    self.contextMenuHandlers.delete(btn);
                }
            });

            const allListItems = popover.querySelectorAll("forge-list-item");
            allListItems.forEach(item =>
            {
                const handler = self.contextMenuHandlers.get(item as HTMLElement);
                if (handler)
                {
                    (item as HTMLElement).removeEventListener("click", handler);
                    self.contextMenuHandlers.delete(item as HTMLElement);
                }
            });
            popover.removeEventListener("forge-popover-toggle", closePopover);
            popover.open = false;
        };

        document.body.appendChild(popover);
        popover.open = true;
        popover.addEventListener("forge-popover-toggle", closePopover);

        (document.querySelector("forge-popover forge-list forge-list-item button") as HTMLElement).focus();
    }

    allowEditInNewTab(): boolean
    {
        return this.$metadataViewService.IsProjectionGrid(this.viewMetadata) && this.$metadataFormService.initialForm;
    }

    edit(row: any, viewOnly: boolean, $event: any = null): void
    {
        this.$gridLayoutService.SetScrollPosition(this.providerId, row, this.actionStrip?.context?.grid);
        if ($event?.ctrlKey)
            this.viewMetadata["$editInNewTab"] = true;
        if (this.$gridLoadService.allowShowRecent(this.viewMetadata))
            this.$gridLoadService.addToRecentIds(this.viewMetadata.GridId, row.Id);
        if (this.cellEdit)
            this.editSingle(row);
        else if (this.useRepository)
        {
            if (this.multipleEdit && (this.$providerStorageService.LocalEntitiesStore[this.providerId]?.length > 0 || this.$metadataViewService.IsProjectionGrid(this.viewMetadata)))
                this.editMultiple(row, viewOnly);
            else
                this.editSingle(row);
        }
    }

    editSingle(row: any): void {
        this.repositoryFunction.emit(<RowCommandArgs>{
            command: "Edit",
            row: <RowCommandRow>{
                id: row.Id,
                model: row["$$model"],
            },
        });
    }

    editMultiple(row: any, viewOnly: boolean): void
    {
        if (!this.viewMetadata.EditAction)
            this.$multipleEntityEditService.ProviderId = this.providerId;

        let entities: any[];

        if (this.$gridSelectionService.SelectedCount[this.providerId] > 1)
        {
            const selectedEntities: any[] = this.actionStrip?.context?.grid.data
                ?.filter(r => {
                    const canOpen =
                        viewOnly ||
                        this.$metadataTableService.CanDo("Edit", r["$$model"], this.isViewOnly, this.viewMetadata);
                    return this.$gridSelectionService.SelectedIds[this.providerId].indexOf(r.Id) > -1 && canOpen;
                })
                .map(r => r["$$model"]);

            entities = selectedEntities;
            entities.forEach(x => x["$selectionEdit"] = true);
        }
        else
        {
            entities = this.$providerStorageService.LocalEntitiesStore[this.providerId].filter(e =>
            {
                const isInFilter = e["$inFilter"] && e.SerializedRowState !== "Deleted";
                const canOpen = viewOnly || this.$metadataTableService.CanDo("Edit", e, this.isViewOnly, this.viewMetadata);
                return canOpen && isInFilter;
            });
        }
        if (!this.viewMetadata.EditAction)
        {
            this.$multipleEntityEditService.EditableEntities = entities;
            this.$multipleEntityEditService.CurrentEditIndex = this.$multipleEntityEditService.EditableEntities.indexOf(row["$$model"]);
        }
        else
            this.$multipleEntityEditService.editActionEntity = row["$$model"];

        this.repositoryFunction.emit(<RowCommandArgs>{ command: "EditMultiple" });
    }

    delete(row: any): void {
        let msg = this.viewMetadata.DeleteMessage;
        if (Utilities.IsNullOrUndefinedOrEmpty(msg)) {
            msg = [
                "Are you sure you want to delete ",
                row["$$model"]
                    ? row["$$model"]["MetadataFormTitle"]
                        ? row["$$model"]["MetadataFormTitle"]
                        : "this item"
                    : "this item",
                "?",
            ].join("");
        }
        this.$commonDialogsService.showConfirmationMessage(msg, "Delete Item").then(() => {
            if (this.useRepository) {
                row["$$Deleting"] = true;
                this.repositoryFunction.emit(<RowCommandArgs>{
                    command: "Delete",
                    row: <RowCommandRow>{
                        id: row.Id,
                        model: row["$$model"],
                    },
                });
            }
        });
    }

    canDo(type: string): boolean {
        const model = this.row["$$model"];

        if (this.cellEdit && type === "Edit")
        {
            let editableColumn = false;
            if (!Utilities.IsNullOrUndefined(this.viewMetadata))
            {
                const columns = this.viewMetadata["$columns"].map(x=> x.Name);
                for(let i = 0, l = columns.length; i < l; i++) //Use old-fashioned for-loop so we can break when there's an editable column
                {
                    let prop = this.viewMetadata.GridProperties.ClassMetadata.Properties.find(x => x.Name === columns[i]);
                    if(prop)
                    {
                        if (!this.$metadataPropertyService.isViewOnly(prop, model, model["$$Parent"]))
                        {
                            editableColumn = true;
                            break;
                        }
                    }
                }
            }

            if (!editableColumn)
                return false;
        }
        return this.$metadataTableService.CanDo(type, model, this.isViewOnly, this.viewMetadata);
    }

    overflowMenu($event: any): void
    {
        $event.preventDefault();
        let elements = document.getElementsByTagName("row-actions");
        if (!elements || elements.length === 0)
            return;
        this.overFlowMenu.anchorElement = <HTMLElement>document.getElementsByTagName("row-actions")[0];
        requestAnimationFrame(() => this.overFlowMenu.open = true);
    }

    actionLabel(label: string): string
    {
        return this.$metadataService.EvaluateString(label, this.model["$$Parent"], this.model);
    }

    onActionExecuted(): void
    {
        this.$gridLayoutService.SetScrollPosition(this.providerId, this.row, this.actionStrip?.context?.grid);
    }
}
